/**
 * Dashboard Page
 * Modern, professional design with glassmorphism effects
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUser, useClerk, OrganizationSwitcher, useOrganization } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { Logo } from '../components/Logo';
import { Button } from '@/components/ui/button';
import { Alert } from '../components/Alert';
import { useUserApps } from '@/hooks/use-user-apps';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { API_ENDPOINTS } from '@/lib/config';
import { ManageAppOrganizationsModal } from '@/components/ManageAppOrganizationsModal';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ArrowRight, Lock, Sparkles, Settings } from 'lucide-react';

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirect_url');
  const { t } = useTranslation();
  const { isLoaded, isSignedIn, user } = useUser();
  const { signOut } = useClerk();
  const { getToken } = useAuth();
  const { organization } = useOrganization();
  const [selectedApp, setSelectedApp] = useState<any>(null);

  // Handle redirect_url if present
  useEffect(() => {
    if (isLoaded && isSignedIn && redirectUrl) {
      window.location.href = redirectUrl;
    }
  }, [isLoaded, isSignedIn, redirectUrl]);

  // React Query hook (auto-refetches when organization changes)
  const { data, isLoading, error } = useUserApps();

  // Récupérer le profil utilisateur avec permissions
  const { data: profileData } = useQuery({
    queryKey: ['user', 'profile'],
    queryFn: async () => {
      const token = await getToken();
      const response = await fetch(API_ENDPOINTS.userProfile, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch profile');
      return response.json();
    },
    enabled: isSignedIn,
  });

  const isSuperAdmin = profileData?.permissions?.isSuperAdmin || false;

  useEffect(() => {
    if (!isLoaded) return;

    if (!isSignedIn) {
      navigate('/login');
      return;
    }
  }, [isLoaded, isSignedIn, navigate]);

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const handleAppClick = (app: any) => {
    if (app.hasAccess) {
      window.location.href = `https://${app.domain}`;
    }
  };

  if (!isLoaded || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="text-center">
          <div className="spinner h-12 w-12 mx-auto mb-4" />
          <p className="text-slate-600 font-medium">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!isSignedIn) {
    return null;
  }

  // If we are redirecting, don't render the dashboard content
  if (redirectUrl) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="text-center">
          <div className="spinner h-12 w-12 mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Redirecting back to application...</p>
        </div>
      </div>
    );
  }

  const apps = data?.apps || [];
  // Sort apps: accessible first, then locked
  const sortedApps = [...apps].sort((a, b) => {
    if (a.hasAccess === b.hasAccess) return 0;
    return a.hasAccess ? -1 : 1;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header - Glassmorphism */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-slate-200/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Logo size="sm" />
            </div>

            <div className="flex items-center gap-4">
              {/* Language Switcher */}
              <LanguageSwitcher />

              {/* Organization Switcher */}
              <OrganizationSwitcher
                hidePersonal={true}
                organizationProfileMode="modal"
                organizationProfileProps={{
                  appearance: {
                    elements: {
                      rootBox: 'rounded-xl',
                    },
                  },
                }}
                appearance={{
                  elements: {
                    rootBox: 'flex items-center',
                    organizationSwitcherTrigger:
                      'px-4 py-2.5 rounded-xl border border-slate-200 hover:border-slate-300 bg-white/80 hover:bg-white transition-all duration-200 shadow-sm hover:shadow',
                    organizationSwitcherPopoverActionButton__createOrganization: 'hidden',
                  },
                }}
              />

              <div className="hidden lg:flex items-center gap-3 pl-4 border-l border-slate-200">
                <div className="text-right">
                  <p className="text-sm font-semibold text-slate-900">
                    {user?.fullName || user?.firstName}
                  </p>
                  <p className="text-xs text-slate-500">
                    {user?.primaryEmailAddress?.emailAddress}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className="hover:bg-slate-100"
                >
                  {t('common.logout')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        {error && (
          <Alert
            type="error"
            message={error instanceof Error ? error.message : 'Failed to load apps'}
            onClose={() => {}}
          />
        )}

        {/* No Organization Alert */}
        {!organization && (
          <div className="mb-8 p-6 bg-yellow-50 border-2 border-yellow-200 rounded-2xl">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <svg
                  className="w-6 h-6 text-yellow-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-yellow-900 mb-1">
                  {t('dashboard.noOrganization.title')}
                </h3>
                <p className="text-sm text-yellow-800">
                  {t('dashboard.noOrganization.description')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* All Apps Grid - Sorted with accessible first */}
        {sortedApps.length > 0 && (
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-10">
              {sortedApps.map((app, index) => {
                // Convert hex color to RGB for dynamic borders
                const hexToRgb = (hex: string) => {
                  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
                  return result
                    ? {
                        r: parseInt(result[1], 16),
                        g: parseInt(result[2], 16),
                        b: parseInt(result[3], 16),
                      }
                    : { r: 59, g: 130, b: 246 }; // Default blue
                };

                const appColor = app.color || '#3B82F6';
                const rgb = hexToRgb(appColor);
                const rgbaString = `${rgb.r}, ${rgb.g}, ${rgb.b}`;

                return (
                  <div
                    key={app.id}
                    className={`group relative overflow-hidden rounded-3xl backdrop-blur-sm p-8 min-h-[320px] transition-all duration-500 ${
                      app.hasAccess
                        ? 'bg-gradient-to-br from-white/90 to-blue-50/50 hover:scale-[1.02] hover:shadow-2xl cursor-pointer'
                        : 'bg-white/40 opacity-60'
                    }`}
                    style={{
                      animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`,
                      boxShadow: app.hasAccess
                        ? `
                          0 0 0 2px rgba(${rgbaString}, 0.5),
                          0 0 0 4px rgba(255, 255, 255, 0.1),
                          0 0 0 10px rgba(${rgbaString}, 0.15)
                        `
                        : `
                          0 0 0 1px rgba(148, 163, 184, 0.15),
                          0 0 0 2px rgba(255, 255, 255, 0.1),
                          0 0 0 3px rgba(148, 163, 184, 0.1)
                        `,
                      ['--app-color' as any]: appColor,
                      ['--app-color-rgb' as any]: rgbaString,
                    }}
                    onClick={() => handleAppClick(app)}
                  >
                    {/* Technical Grid Background - Only for accessible apps */}
                    {app.hasAccess && (
                      <>
                        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                          <div
                            className="absolute inset-0"
                            style={{
                              backgroundImage: `
                              linear-gradient(to right, rgba(${rgbaString}, 0.1) 1px, transparent 1px),
                              linear-gradient(to bottom, rgba(${rgbaString}, 0.1) 1px, transparent 1px)
                            `,
                              backgroundSize: '40px 40px',
                            }}
                          />
                        </div>
                        {/* Gradient Glow Effect */}
                        <div
                          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                          style={{
                            background: `linear-gradient(135deg, rgba(${rgbaString}, 0) 0%, rgba(${rgbaString}, 0.05) 50%, rgba(${rgbaString}, 0.1) 100%)`,
                          }}
                        />
                      </>
                    )}

                    {/* Super Admin Settings Button */}
                    {isSuperAdmin && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedApp(app);
                        }}
                        style={{
                          backgroundColor: app.color || '#9333EA',
                        }}
                        className="absolute top-4 right-4 flex items-center justify-center w-10 h-10 rounded-lg text-white shadow-lg hover:shadow-xl transition-all duration-200 z-10 hover:opacity-90"
                        title={t('superAdmin.manageAccess')}
                      >
                        <Settings className="w-5 h-5" />
                      </button>
                    )}

                    {/* Lock Icon Badge - Only for locked apps */}
                    {!app.hasAccess && !isSuperAdmin && (
                      <div className="absolute top-4 right-4 flex items-center justify-center w-8 h-8 rounded-lg bg-slate-200/80 backdrop-blur-sm">
                        <Lock className="w-4 h-4 text-slate-600" />
                      </div>
                    )}

                    {/* Content */}
                    <div className="relative flex flex-col items-center text-center justify-between h-full">
                      {/* App Logo with Glow Effect */}
                      <div className="relative mb-6 flex items-center justify-center w-full">
                        {app.logoUrl ? (
                          <div
                            className={`w-16 h-16 flex items-center justify-center ${!app.hasAccess && 'grayscale'}`}
                          >
                            <img
                              src={app.logoUrl}
                              alt={`${app.name} logo`}
                              className={`w-full h-full object-contain ${app.hasAccess && 'group-hover:scale-110'} transition-transform duration-500`}
                            />
                          </div>
                        ) : (
                          <div
                            className={`w-16 h-16 rounded-2xl flex items-center justify-center text-white font-bold text-2xl shadow-lg ring-4 ${
                              app.hasAccess
                                ? 'ring-white/50 group-hover:ring-blue-500/30'
                                : 'ring-slate-200/30 grayscale'
                            } transition-all duration-300`}
                            style={{
                              backgroundColor: app.color || (app.hasAccess ? '#3B82F6' : '#9CA3AF'),
                            }}
                          >
                            {app.name.charAt(0)}
                          </div>
                        )}
                        {/* Glow Effect - Only for accessible apps */}
                        {app.hasAccess && (
                          <div
                            className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500 -z-10"
                            style={{ backgroundColor: appColor }}
                          />
                        )}
                      </div>

                      {/* App Info */}
                      <div className="flex-1 flex flex-col justify-center w-full">
                        <h3
                          className={`text-xl font-bold mb-2 transition-colors ${
                            app.hasAccess
                              ? 'text-slate-900 group-hover:text-blue-600'
                              : 'text-slate-700'
                          }`}
                        >
                          {app.name}
                        </h3>
                        {app.description && (
                          <p
                            className={`text-sm mb-3 line-clamp-2 leading-relaxed px-2 ${
                              app.hasAccess ? 'text-slate-600' : 'text-slate-500'
                            }`}
                          >
                            {app.description}
                          </p>
                        )}
                        <p className="text-xs text-slate-400 font-medium">{app.domain}</p>
                      </div>

                      {/* Launch Button or Locked Badge */}
                      {app.hasAccess ? (
                        <div
                          className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-white opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 shadow-lg"
                          style={{
                            background: `linear-gradient(135deg, ${appColor} 0%, ${appColor}dd 100%)`,
                            boxShadow: `0 4px 14px 0 rgba(${rgbaString}, 0.4)`,
                          }}
                        >
                          <span className="text-sm font-semibold">
                            {t('dashboard.clickToAccess')}
                          </span>
                          <ArrowRight className="w-4 h-4" />
                        </div>
                      ) : (
                        <div className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-slate-300 text-slate-600">
                          <Lock className="w-4 h-4" />
                          <span className="text-sm font-semibold">{t('dashboard.lockedApp')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty State */}
        {sortedApps.length === 0 && (
          <div className="text-center py-24">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-3xl bg-gradient-to-br from-blue-500/10 to-indigo-500/10 mb-6">
              <Sparkles className="w-12 h-12 text-blue-600" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">
              {t('dashboard.noApps.title')}
            </h3>
            <p className="text-slate-600">{t('dashboard.noApps.description')}</p>
          </div>
        )}
      </main>

      {/* Super Admin Modal */}
      {selectedApp && (
        <ManageAppOrganizationsModal app={selectedApp} onClose={() => setSelectedApp(null)} />
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        /* Enhanced border effect on hover - uses dynamic app color */
        button:not(:disabled):hover {
          box-shadow: 
            0 0 0 1px rgba(var(--app-color-rgb), 0.3),
            0 0 0 2px rgba(255, 255, 255, 0.2),
            0 0 0 3px rgba(var(--app-color-rgb), 0.4),
            0 20px 25px -5px rgba(var(--app-color-rgb), 0.1),
            0 10px 10px -5px rgba(var(--app-color-rgb), 0.04) !important;
        }
      `}</style>
    </div>
  );
};
